'use client';

import { Paragraph } from '@koda/koda-components/atoms/Paragraph';
import { DatePicker } from '@koda/koda-components/molecules/DatePicker';
import NumberField from '@koda/koda-components/molecules/NumberField';
import { RadioGroup } from '@koda/koda-components/molecules/RadioGroup';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { ReactElement } from 'react';
import { LicenseTypes } from '../../../types/licenses';
import { useLicenseBusiness } from '../hooks/useLicenseBusiness';
import { Past5YearsFuture2Years } from './consts/dateIntervals';
import { createFitnessCenterDefaults, FITNESS_CENTER_CONTENT_PATH, fitnessCenterSchema } from './fitness.Schema';

export default function FitnessCenterComponent(): ReactElement {
  const licenseCode = LicenseTypes.FitnessCenterAndTreatment;

  const { values, setField, getError, costCalculated, showErrors } = useLicenseBusiness(licenseCode, {
    schema: fitnessCenterSchema,
    defaults: createFitnessCenterDefaults,
  });

  const t = useTranslations(FITNESS_CENTER_CONTENT_PATH);

  return (
    <>
      <NumberField
        label={t('venueSize.label')}
        name="area"
        placeholder={t('venueSize.placeholder')}
        required
        disabled={costCalculated}
        value={values.area}
        onChange={e => setField('area', parseInt(e.target.value))}
        helpText={t('venueSize.helpText')}
        error={values.area || showErrors ? getError('area') : undefined}
        validated={!!values.area && !getError('area')}
        min={1}
        sign="positive-only"
        styleVariant="border"
      />
      <DatePicker
        label={t('startDate.label')}
        name="startDate"
        placeholder={t('startDate.placeholder')}
        disabled={costCalculated}
        value={values.startDate && values.startDate !== '' ? new Date(values.startDate) : undefined}
        onChange={date => setField('startDate', date ? dayjs(date).format('YYYY-MM-DD') : '')}
        error={values.startDate || showErrors ? getError('startDate') : undefined}
        validated={!!values.startDate && !getError('startDate')}
        initialVisibleValue={new Date()}
        interval={Past5YearsFuture2Years}
      />
      <div className="flex flex-col gap-y-4">
        <h2 className="font-graphik text-xl font-medium">{t('groupTrainingSection.title')}</h2>
        <Paragraph text={t('groupTrainingSection.description')} />
        <RadioGroup
          title={t('groupTraining.label')}
          options={[
            { value: 'true', label: t('groupTraining.options.yes') },
            { value: 'false', label: t('groupTraining.options.no') },
          ]}
          disabled={costCalculated}
          horizontal
          value={values.hasGroupTraining ? 'true' : 'false'}
          onValueChange={val => {
            const isGroupTraining = val === 'true';
            setField('hasGroupTraining', isGroupTraining);
            // Clear class fields when switching to no group training
            if (!isGroupTraining) {
              setField('classesUnder20', undefined);
              setField('classesOver20', undefined);
            }
          }}
        />
      </div>
      {values.hasGroupTraining && (
        <>
          <NumberField
            label={t('classesUnder20.label')}
            name="classesUnder20"
            placeholder={t('classesUnder20.placeholder')}
            disabled={costCalculated}
            value={values.classesUnder20}
            onChange={e => setField('classesUnder20', parseInt(e.target.value))}
            helpText={t('classesUnder20.helpText')}
            error={values.classesUnder20 !== undefined || showErrors ? getError('classesUnder20') : undefined}
            validated={values.classesUnder20 !== undefined && !getError('classesUnder20')}
            min={1}
            sign="positive-only"
            styleVariant="border"
          />
          <NumberField
            label={t('classesOver20.label')}
            name="classesOver20"
            placeholder={t('classesOver20.placeholder')}
            disabled={costCalculated}
            value={values.classesOver20}
            onChange={e => setField('classesOver20', parseInt(e.target.value))}
            helpText={t('classesOver20.helpText')}
            error={values.classesOver20 !== undefined || showErrors ? getError('classesOver20') : undefined}
            validated={values.classesOver20 !== undefined && !getError('classesOver20')}
            min={1}
            sign="positive-only"
            styleVariant="border"
          />
        </>
      )}
    </>
  );
}
